import React, { useState, useEffect, useCallback, useMemo } from 'react';
import API from '../api/client';
import PaymentAnalyticsDashboard from './PaymentAnalyticsDashboard'; // LINK NEW COMPONENT HERE
import {
    Box, Typography, Grid, TextField, Button, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Paper, Chip,
    Modal, Fade, Backdrop, IconButton, InputAdornment, LinearProgress, Alert, Snackbar, FormControl, InputLabel,
    Select, MenuItem, Tabs, Tab, Drawer, Divider, List, ListItem, ListItemText
} from '@mui/material';
import {
    Wallet, Plus, X, RefreshCw, TrendingUp, Search, Coins, FileText, Printer, Briefcase
} from 'lucide-react';
import {
    ResponsiveContainer, ComposedChart, Area, Bar, XAxis, YAxis,
    Tooltip, PieChart, Pie, Cell, CartesianGrid
} from 'recharts';

const Payments = () => {
    // ─── STATE MANAGEMENT ───
    const [ledger, setLedger] = useState([]);
    const [summary, setSummary] = useState({ gross_revenue_booked: 0, total_cash_collected: 0, outstanding_receivables: 0 });
    const [convertedLeads, setConvertedLeads] = useState([]);
    const [chartData, setChartData] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');

    // TAB NAVIGATION: 0 = Core Operations, 1 = Admin & HR Advanced Monthly Tracker Dashboard
    const [activeWorkspaceTab, setActiveWorkspaceTab] = useState(0);
    const [currentTableFilterTab, setCurrentTableFilterTab] = useState(0);

    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isEditingBalance, setIsEditingBalance] = useState(false);

    // DRILL DOWN DRAWER & MODAL STATES
    const [selectedDrawerTx, setSelectedDrawerTx] = useState(null);
    const [receiptOpen, setReceiptOpen] = useState(false);
    const [selectedReceipt, setSelectedReceipt] = useState(null);

    const [formData, setFormData] = useState({
        paymentId: '', studentName: '', email: '', courseName: '',
        totalFees: '', amountPaid: '', remainingBalance: ''
    });

    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

    // ─── DYNAMIC ROLE EXTRACTION ───
    const rawRole = localStorage.getItem('user_role') || '';
    const currentUser = localStorage.getItem('user_id') || 'System User';
    const roleLower = String(rawRole).trim().toLowerCase();

    const isAdmin = roleLower === 'admin' || roleLower === 'hr';
    const isBde = roleLower === 'bde';

    const showToast = (message, severity = 'success') => {
        setToast({ open: true, message, severity });
    };

    const closeAndResetModal = () => {
        setOpen(false);
        setIsEditingBalance(false);
        setFormData({ paymentId: '', studentName: '', email: '', courseName: '', totalFees: '', amountPaid: '', remainingBalance: '' });
    };

    // ─── DATA SYNCHRONIZATION LAYER ───
    const fetchFinancials = useCallback(async () => {
        setLoading(true);
        try {
            const response = await API.get('/payments/ledger');
            let fetchedLedger = response.data.ledger || [];
            let fetchedSummary = response.data.summary || { gross_revenue_booked: 0, total_cash_collected: 0, outstanding_receivables: 0 };

            setLedger(fetchedLedger);
            setSummary(fetchedSummary);

            const structuredTimeline = [...fetchedLedger].slice(0, 10).reverse().map(tx => ({
                date: new Date(tx.recorded_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }),
                Revenue: parseFloat(tx.total_fees || 0),
                Collected: parseFloat(tx.amount_paid || 0)
            }));
            setChartData(structuredTimeline);

            if (roleLower === 'bde') {
                const leadsRes = await API.get('/payments/converted-leads');
                setConvertedLeads(leadsRes.data || []);
            }
        } catch (err) {
            console.error('Data Sync Failed:', err);
            showToast('Failed to sync financial ledgers from server.', 'error');
        } finally {
            setLoading(false);
        }
    }, [roleLower]);

    useEffect(() => {
        fetchFinancials();
    }, [fetchFinancials]);

    const handleRecordPayment = async (e) => {
        e.preventDefault();
        try {
            await API.post('/payments/record', {
                payment_id: formData.paymentId ? String(formData.paymentId) : null,
                student_name: formData.studentName,
                email: formData.email,
                course_name: formData.courseName,
                total_fees: parseFloat(formData.totalFees || 0),
                amount_paid: parseFloat(formData.amountPaid || 0),
                collected_by: currentUser
            });

            showToast(isEditingBalance ? 'Installment balanced and audited.' : 'New pipeline intake secured in ledger.');
            closeAndResetModal();
            fetchFinancials();
        } catch (err) {
            showToast(err.response?.data?.message || 'Transaction Pipeline Rejected', 'error');
        }
    };

    const handleSelectLeadChange = (e) => {
        const selectedId = e.target.value;
        const targetLead = convertedLeads.find(l => l.id === selectedId);
        if (targetLead) {
            setFormData({
                paymentId: targetLead.id, studentName: targetLead.student_name || '',
                email: targetLead.email || '', courseName: targetLead.course_name || '',
                totalFees: '', amountPaid: '', remainingBalance: ''
            });
        }
    };

    const openBalanceCollectionMode = (tx, e) => {
        e.stopPropagation();
        setIsEditingBalance(true);
        setFormData({
            paymentId: tx.payment_id || tx.id, studentName: tx.student_name,
            email: tx.email, courseName: tx.course_name, totalFees: tx.total_fees, amountPaid: '',
            remainingBalance: parseFloat(tx.total_fees || 0) - parseFloat(tx.amount_paid || 0)
        });
        setOpen(true);
    };

    const handleViewReceipt = (tx, e) => {
        if (e) e.stopPropagation();
        setSelectedReceipt(tx);
        setReceiptOpen(true);
    };

    const handlePrintReceipt = () => { window.print(); };

    const filteredLedger = useMemo(() => {
        return ledger.filter(tx => {
            const matchesSearch =
                tx.student_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                tx.course_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                tx.collected_by?.toLowerCase().includes(searchQuery.toLowerCase());

            const statusNorm = String(tx.payment_status).toLowerCase();
            if (currentTableFilterTab === 1) return matchesSearch && (statusNorm.includes('fully') || statusNorm === 'paid');
            if (currentTableFilterTab === 2) return matchesSearch && statusNorm.includes('partial');
            return matchesSearch;
        });
    }, [ledger, searchQuery, currentTableFilterTab]);

    const collectionVelocity = useMemo(() => {
        if (!summary.gross_revenue_booked) return 0;
        return ((summary.total_cash_collected / summary.gross_revenue_booked) * 100).toFixed(1);
    }, [summary]);

    const pieData = [
        { name: 'Collected Revenue', value: summary.total_cash_collected, color: '#00a124' },
        { name: 'Outstanding Pipeline Balance', value: summary.outstanding_receivables, color: '#e52222' }
    ];

    const getStatusStyles = (status) => {
        const norm = String(status).toLowerCase();
        if (norm.includes('fully') || norm === 'paid') return { bg: '#eafbe7', text: '#0f7d11', border: '#bcf0b6' };
        if (norm.includes('partial')) return { bg: '#fff9e6', text: '#b27b00', border: '#ffeaa3' };
        return { bg: '#fdf2f2', text: '#cb2a2a', border: '#fbcbcb' };
    };

    return (
        <Box sx={{ bgcolor: '#f3f5f9', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            {loading && <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 3000, height: 3, bgcolor: '#cfd7e6', '& .MuiLinearProgress-bar': { bgcolor: '#0176d3' } }} />}

            {/* STICKY CORE HEADER CONFIGURATION */}
            <Box sx={{ position: 'sticky', top: 0, zIndex: 1100, bgcolor: '#ffffff', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                <Box sx={{ borderBottom: '1px solid #d8dde6', p: '12px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ bgcolor: '#0176d3', p: 1.5, borderRadius: '4px', color: '#fff', display: 'flex' }}><Briefcase size={18} /></Box>
                        <Box>
                            <Typography variant="caption" sx={{ color: '#514f4d', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '11px' }}>Revenue Intelligence Cloud</Typography>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#081c3b', mt: -0.5 }}>
                                Salesforce Revenue Workspace • {currentUser} ({rawRole})
                            </Typography>
                        </Box>
                    </Box>

                    {/* Salesforce Tab-Switcher Architecture */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Tabs value={activeWorkspaceTab} onChange={(e, nv) => setActiveWorkspaceTab(nv)} sx={{ minHeight: '36px', '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, minHeight: '36px', py: 0.5, fontSize: '13px' } }}>
                            <Tab label="Operational Ledgers" />
                            {isAdmin && <Tab label="Employee Performance Dashboard" />}
                        </Tabs>
                        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                        <Button variant="outlined" onClick={fetchFinancials} startIcon={<RefreshCw size={14} />} sx={{ borderRadius: '4px', textTransform: 'none', fontWeight: 600, borderColor: '#dddbda', color: '#0176d3' }}>Sync Node</Button>
                        {isBde && (
                            <Button variant="contained" onClick={() => { setIsEditingBalance(false); setOpen(true); }} startIcon={<Plus size={16} />} sx={{ borderRadius: '4px', textTransform: 'none', fontWeight: 600, bgcolor: '#0176d3', boxShadow: 'none' }}>Log Transaction</Button>
                        )}
                    </Box>
                </Box>

                {/* Sub-Header KPI Ribbon */}
                <Box sx={{ borderBottom: '1px solid #d8dde6', px: 3, py: 1.5, bgcolor: '#fafaf9' }}>
                    <Grid container spacing={0} sx={{ '& > div': { borderRight: '1px solid #dddbda', px: 3, '&:last-child': { borderRight: 'none' } } }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="caption" sx={{ color: '#514f4d', display: 'block', fontWeight: 600 }}>GROSS BOOKED REVENUE</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#0176d3', mt: 0.5 }}>₹{Number(summary.gross_revenue_booked).toLocaleString('en-IN')}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="caption" sx={{ color: '#514f4d', display: 'block', fontWeight: 600 }}>REALIZED CASH COLLECTED</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#2e7d32', mt: 0.5 }}>₹{Number(summary.total_cash_collected).toLocaleString('en-IN')}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="caption" sx={{ color: '#514f4d', display: 'block', fontWeight: 600 }}>UNREALIZED PIPELINE BALANCE</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#d32f2f', mt: 0.5 }}>₹{Number(summary.outstanding_receivables).toLocaleString('en-IN')}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="caption" sx={{ color: '#514f4d', display: 'block', fontWeight: 600 }}>COLLECTION VELOCITY</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: '#081c3b' }}>{collectionVelocity}%</Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Box>

            {/* DYNAMIC WORKSPACE PORTAL VIEW SWITCHER */}
            <Box sx={{ p: 3, flexGrow: 1 }}>
                {activeWorkspaceTab === 1 && isAdmin ? (
                    /* RENDER SPLIT COMPONENT FOR ADMIN / HR TRACKING */
                    <PaymentAnalyticsDashboard ledger={ledger} onDrillDownTransaction={(tx) => setSelectedDrawerTx(tx)} />
                ) : (
                    /* DEFAULT OPERATIONAL VIEW ENTRY */
                    <>
                        <Grid container spacing={3} sx={{ mb: 4 }}>
                            <Grid item xs={12} lg={8}>
                                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: '4px', bgcolor: '#fff', borderColor: '#d8dde6' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#081c3b', display: 'flex', alignItems: 'center', gap: 1 }}><TrendingUp size={16} /> Business Volume Analysis</Typography>
                                    </Box>
                                    <Box sx={{ width: '100%', height: 200 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <ComposedChart data={chartData} margin={{ top: 10, right: 5, left: -20, bottom: 0 }}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#f3f5f9" />
                                                <XAxis dataKey="date" tick={{ fill: '#706e6b', fontSize: 11 }} />
                                                <YAxis tick={{ fill: '#706e6b', fontSize: 11 }} />
                                                <Tooltip contentStyle={{ background: '#081c3b', borderRadius: '4px', color: '#fff', fontSize: '12px' }} />
                                                <Area type="monotone" name="Gross Volume" dataKey="Revenue" stroke="#0176d3" strokeWidth={2} fillOpacity={0.05} fill="#0176d3" />
                                                <Bar dataKey="Collected" name="Liquids Secured" barSize={20} fill="#2e7d32" radius={[4, 4, 0, 0]} />
                                            </ComposedChart>
                                        </ResponsiveContainer>
                                    </Box>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} lg={4}>
                                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: '4px', bgcolor: '#fff', borderColor: '#d8dde6', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxSizing: 'border-box' }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#081c3b' }}>Allocation Metrics</Typography>
                                    <Box sx={{ width: '100%', height: 120, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie data={pieData} cx="50%" cy="50%" innerRadius={35} outerRadius={50} paddingAngle={5} dataKey="value">
                                                    {pieData.map((entry, idx) => <Cell key={`cell-${idx}`} fill={entry.color} />)}
                                                </Pie>
                                                <Tooltip formatter={(v) => `₹${v.toLocaleString('en-IN')}`} />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </Box>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        {pieData.map((item) => (
                                            <Box key={item.name} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 0.8, bgcolor: '#f3f5f9', borderRadius: '4px' }}>
                                                <Typography variant="caption" sx={{ color: '#514f4d', fontWeight: 600 }}>{item.name}</Typography>
                                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#081c3b' }}>{summary.gross_revenue_booked > 0 ? ((item.value / summary.gross_revenue_booked) * 100).toFixed(1) : 0}%</Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </Paper>
                            </Grid>
                        </Grid>

                        {/* WORKSPACE DATA ROW LEDGER WINDOW */}
                        <Box sx={{ bgcolor: '#ffffff', border: '1px solid #d8dde6', borderRadius: '4px 4px 0 0', p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                            <Tabs value={currentTableFilterTab} onChange={(e, nv) => setCurrentTableFilterTab(nv)} sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, fontSize: '13px' } }}>
                                <Tab label="All Entries" />
                                <Tab label="Fully Settled" />
                                <Tab label="Partial Balances" />
                            </Tabs>
                            <TextField
                                placeholder="Filter ledger parameters..." variant="outlined" size="small" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                                sx={{ bgcolor: '#fff', width: 280 }}
                                InputProps={{ startAdornment: (<InputAdornment position="start"><Search size={14} /></InputAdornment>) }}
                            />
                        </Box>

                        <TableContainer component={Paper} sx={{ borderRadius: '0 0 4px 4px', border: '1px solid #d8dde6', borderTop: 'none', boxShadow: 'none' }}>
                            <Table size="small">
                                <TableHead sx={{ bgcolor: '#fafaf9' }}>
                                    <TableRow sx={{ '& th': { fontWeight: 700, color: '#514f4d', fontSize: '11px' } }}>
                                        <TableCell>Tx Reference</TableCell>
                                        <TableCell>Client Particulars</TableCell>
                                        <TableCell>Asset Track</TableCell>
                                        <TableCell>Contract Valuation</TableCell>
                                        <TableCell>Realized Cash</TableCell>
                                        <TableCell>Balance Receivable</TableCell>
                                        <TableCell>Agent</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell align="center">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredLedger.length === 0 ? (
                                        <TableRow><TableCell colSpan={9} align="center" sx={{ py: 6 }}><Typography variant="body2" sx={{ color: '#706e6b' }}>No data sets matched this window configuration.</Typography></TableCell></TableRow>
                                    ) : (
                                        filteredLedger.map((tx) => {
                                            const cStyle = getStatusStyles(tx.payment_status);
                                            const bal = parseFloat(tx.total_fees || 0) - parseFloat(tx.amount_paid || 0);
                                            return (
                                                <TableRow key={tx.id} hover onClick={() => setSelectedDrawerTx(tx)} sx={{ cursor: 'pointer' }}>
                                                    <TableCell sx={{ fontWeight: 600, color: '#0176d3' }}>#{tx.id}</TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{tx.student_name}</Typography>
                                                        <Typography variant="caption" sx={{ color: '#706e6b' }}>{tx.email}</Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 600 }}>{tx.course_name}</TableCell>
                                                    <TableCell>₹{Number(tx.total_fees || 0).toLocaleString('en-IN')}</TableCell>
                                                    <TableCell sx={{ color: '#2e7d32', fontWeight: 700 }}>₹{Number(tx.amount_paid || 0).toLocaleString('en-IN')}</TableCell>
                                                    <TableCell sx={{ color: bal > 0 ? '#d32f2f' : '#2e7d32', fontWeight: 700 }}>₹{Number(bal).toLocaleString('en-IN')}</TableCell>
                                                    <TableCell sx={{ fontSize: '12px' }}>{tx.collected_by || tx.recorded_by || "System Automated"}</TableCell>
                                                    <TableCell><Chip label={tx.payment_status} size="small" sx={{ fontWeight: 700, borderRadius: '3px', fontSize: '10px', bgcolor: cStyle.bg, color: cStyle.text, border: `1px solid ${cStyle.border}` }} /></TableCell>
                                                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                                                        {isBde && bal > 0 ? (
                                                            <Button variant="outlined" size="small" startIcon={<Coins size={12} />} onClick={(e) => openBalanceCollectionMode(tx, e)} sx={{ textTransform: 'none', fontSize: '11px', py: 0.2 }}>Collect</Button>
                                                        ) : (
                                                            <Button variant="contained" size="small" startIcon={<FileText size={12} />} onClick={(e) => handleViewReceipt(tx, e)} sx={{ textTransform: 'none', fontSize: '11px', py: 0.2, bgcolor: '#0176d3', boxShadow: 'none' }}>Receipt</Button>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )}
            </Box>

            {/* CONTEXTUAL OBJECT EXTRACTION DRILL DOWN DRAWER */}
            <Drawer anchor="right" open={Boolean(selectedDrawerTx)} onClose={() => setSelectedDrawerTx(null)}>
                <Box sx={{ width: 400, p: 3, height: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' }}>
                    {selectedDrawerTx && (
                        <>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6" sx={{ fontWeight: 800 }}>Object Profile Drill-Down</Typography>
                                <IconButton onClick={() => setSelectedDrawerTx(null)} size="small"><X size={18} /></IconButton>
                            </Box>
                            <Divider sx={{ mb: 2 }} />
                            <Box sx={{ bgcolor: '#f3f5f9', p: 2, borderRadius: '4px', mb: 3, borderLeft: '4px solid #0176d3' }}>
                                <Typography variant="caption" sx={{ color: '#514f4d', display: 'block', fontWeight: 600 }}>SYSTEM RECORD BLOCK INDEX</Typography>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>#{selectedDrawerTx.id}</Typography>
                            </Box>
                            <List disablePadding sx={{ border: '1px solid #e2e8f0', borderRadius: '4px', mb: 3 }}>
                                <ListItem><ListItemText primary="Client Name" secondary={selectedDrawerTx.student_name} primaryTypographyProps={{ fontSize: '12px' }} secondaryTypographyProps={{ fontWeight: 700, color: '#081c3b' }} /></ListItem>
                                <Divider />
                                <ListItem><ListItemText primary="Endpoint Comms" secondary={selectedDrawerTx.email} primaryTypographyProps={{ fontSize: '12px' }} secondaryTypographyProps={{ fontWeight: 600 }} /></ListItem>
                                <Divider />
                                <ListItem><ListItemText primary="Target Track" secondary={selectedDrawerTx.course_name} primaryTypographyProps={{ fontSize: '12px' }} secondaryTypographyProps={{ fontWeight: 700, color: '#0176d3' }} /></ListItem>
                            </List>
                            <Button fullWidth variant="contained" startIcon={<FileText size={16} />} onClick={() => handleViewReceipt(selectedDrawerTx, null)} sx={{ bgcolor: '#081c3b', textTransform: 'none', mt: 'auto', boxShadow: 'none' }}>Launch Receipt</Button>
                        </>
                    )}
                </Box>
            </Drawer>

            {/* PAYMENT LOGGING TRANSACTION MODAL */}
            {isBde && (
                <Modal open={open} onClose={closeAndResetModal} closeAfterTransition BackdropComponent={Backdrop} BackdropProps={{ timeout: 200 }}>
                    <Fade in={open}>
                        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 460, bgcolor: '#ffffff', p: 4, borderRadius: '4px', border: '1px solid #d8dde6', boxShadow: '0 20px 40px rgba(0,0,0,0.15)' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>{isEditingBalance ? "Collect Asset Balance" : "Log New Contract Ledger Record"}</Typography>
                                <IconButton onClick={closeAndResetModal} size="small"><X size={18} /></IconButton>
                            </Box>
                            <form onSubmit={handleRecordPayment} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                {!isEditingBalance && (
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Select CRM Lead Profile</InputLabel>
                                        <Select label="Select CRM Lead Profile" onChange={handleSelectLeadChange} value={convertedLeads.find(l => l.student_name === formData.studentName)?.id || ""}>
                                            {convertedLeads.map(lead => <MenuItem key={lead.id} value={lead.id}>{lead.student_name} — ({lead.course_name})</MenuItem>)}
                                        </Select>
                                    </FormControl>
                                )}
                                <TextField fullWidth label="Client Track Name" value={formData.studentName} disabled size="small" />
                                <TextField fullWidth label="Asset Scope Track" value={formData.courseName} disabled size="small" />
                                {formData.remainingBalance && <Alert severity="warning" icon={<Wallet size={16} />} sx={{ py: 0 }}>Outstanding Receivable: <strong>₹{formData.remainingBalance}</strong></Alert>}
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <TextField
                                            fullWidth
                                            label="Valuation Fee"
                                            type="number"
                                            size="small"
                                            value={formData.totalFees}
                                            disabled={isEditingBalance} // Keeps it disabled ONLY during balance collections
                                            onChange={(e) => setFormData({ ...formData, totalFees: e.target.value })} // <--- ADD THIS LINE
                                            required
                                            InputProps={{ startAdornment: (<InputAdornment position="start">₹</InputAdornment>) }}
                                        />
                                    </Grid>                                    <Grid item xs={6}><TextField fullWidth label={isEditingBalance ? "Delta Paid" : "Initial Inflow"} type="number" size="small" value={formData.amountPaid} onChange={(e) => setFormData({ ...formData, amountPaid: e.target.value })} required InputProps={{ startAdornment: (<InputAdornment position="start">₹</InputAdornment>) }} /></Grid>
                                </Grid>
                                <Button type="submit" variant="contained" size="large" sx={{ bgcolor: '#0176d3', textTransform: 'none', fontWeight: 700, boxShadow: 'none' }}>Commit Ledger Record</Button>
                            </form>
                        </Box>
                    </Fade>
                </Modal>
            )}

            {/* PRINT PORTAL SYSTEM RECEIPT INVOICES */}
            <Modal open={receiptOpen} onClose={() => setReceiptOpen(false)} closeAfterTransition BackdropComponent={Backdrop} BackdropProps={{ timeout: 200 }}>
                <Fade in={receiptOpen}>
                    <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 400, bgcolor: '#ffffff', p: 4, borderRadius: '4px', border: '1px solid #d8dde6' }}>
                        {selectedReceipt && (
                            <Box>
                                <Box id="printable-receipt-area">
                                    <Box sx={{ textAlign: 'center', mb: 3, pb: 1.5, borderBottom: '2px dashed #dddbda' }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>LEDGER INVOICE RECEIPT</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="caption">TX REF HASH</Typography><Typography variant="body2" sx={{ fontWeight: 700 }}>#{selectedReceipt.id}</Typography></Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="caption">CLIENT TARGET</Typography><Typography variant="body2" sx={{ fontWeight: 700 }}>{selectedReceipt.student_name}</Typography></Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="caption">BDE AGENT ASSIGNED</Typography><Typography variant="body2" sx={{ fontWeight: 700, color: '#2e7d32' }}>{selectedReceipt.collected_by || selectedReceipt.recorded_by || "System Layer"}</Typography></Box>
                                    </Box>
                                    <Box sx={{ bgcolor: '#f3f5f9', p: 2, borderRadius: '4px', border: '1px solid #dddbda', display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="body2">Valuation Fee</Typography><Typography variant="body2" sx={{ fontWeight: 600 }}>₹{Number(selectedReceipt.total_fees || 0).toLocaleString('en-IN')}</Typography></Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography variant="body2" sx={{ color: '#2e7d32' }}>Realized Cash</Typography><Typography variant="body2" sx={{ fontWeight: 700, color: '#2e7d32' }}>₹{Number(selectedReceipt.amount_paid || 0).toLocaleString('en-IN')}</Typography></Box>
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                                    <Button fullWidth variant="outlined" onClick={handlePrintReceipt} startIcon={<Printer size={16} />} sx={{ textTransform: 'none' }}>Print Native Invoice</Button>
                                    <Button fullWidth variant="contained" onClick={() => setReceiptOpen(false)} sx={{ textTransform: 'none', bgcolor: '#081c3b', boxShadow: 'none' }}>Close View</Button>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Fade>
            </Modal>

            {/* GLOBAL SNACKBAR TILES */}
            <Snackbar open={toast.open} autoHideDuration={5000} onClose={() => setToast({ ...toast, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert severity={toast.severity} variant="filled" onClose={() => setToast({ ...toast, open: false })} sx={{ borderRadius: '3px', fontWeight: 600 }}>{toast.message}</Alert>
            </Snackbar>
        </Box>
    );
};

export default Payments;